
import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const token = localStorage.getItem('accessToken');

export default function GarnishmentSection({ caseId, creditors }) {
  const [garnishments, setGarnishments] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [current, setCurrent] = useState({
    creditor: '',
    property_description: '',
    seizure_type: '',
    garnishment_date: '',
    value: ''
  });
  const [loading, setLoading] = useState(false);

  // Fetch existing garnishments on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/cases/${caseId}/sofa-garnishments/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error();
        setGarnishments(await res.json());
      } catch (err) {
        console.error(err);
      }
    })();
  }, [caseId]);

  // Save handler
  async function saveGarnishment(isEdit = false) {
    setLoading(true);
    const url = `${API_URL}/cases/${caseId}/sofa-garnishments/${isEdit ? current.id + '/' : ''}`;
    const method = isEdit ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(current)
      });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      setGarnishments(prev =>
        isEdit
          ? prev.map(g => (g.id === saved.id ? saved : g))
          : [...prev, saved]
      );
      setAddModalOpen(false);
      setEditModalOpen(false);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  // Delete handler
  async function deleteGarnishment(id) {
    if (!window.confirm('Delete this garnishment?')) return;
    try {
      await fetch(`${API_URL}/cases/${caseId}/sofa-garnishments/${id}/`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setGarnishments(prev => prev.filter(g => g.id !== id));
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <section className="p-6 bg-white shadow-lg rounded mb-6">
      <h2 className="text-xl font-semibold mb-4">Question 10: Garnishments and Seizures</h2>
        <p className="text-sm text-gray-600 mb-4">
    List garnishments, foreclosures, levies, reposessions and other seizures from the last year.
  </p>
      {garnishments.length === 0 ? (
        <p>No garnishments found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-4">
          {garnishments.map(item => (
            <div
              key={item.id}
              className="p-4 bg-white shadow rounded cursor-pointer relative hover:shadow-lg transition-shadow"
              onClick={() => {
                setCurrent({
                  id: item.id,
                  creditor: item.creditor?.id || item.creditor,
                  property_description: item.property_description,
                  seizure_type: item.seizure_type,
                  garnishment_date: item.garnishment_date,
                  value: item.value
                });
                setEditModalOpen(true);
              }}
            >
              <button
                onClick={e => {
                  e.stopPropagation();
                  deleteGarnishment(item.id);
                }}
                className="absolute top-2 right-2 text-red-600 hover:text-red-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" clipRule="evenodd" d="M6 2a1 1 0 00-1 1v1H3a1 1 0 000
                    2h14a1 1 0 000-2h-2V3a1 1 0 00-1-1H6zM4
                    6v10a2 2 0 002 2h8a2 2 0 002-2V6H4zm3
                    2a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0 00-2
                    0zm4 0a1 1 0 000 2v6a1 1 0 102 0V8a1 1 0
                    00-2 0z"/>
                </svg>
              </button>
              <h3 className="font-medium text-lg">
                {item.creditor?.account_name || item.creditor}
              </h3>
              <p className="mt-2 text-sm"><strong>Type:</strong> {item.seizure_type}</p>
              <p className="mt-1 text-sm"><strong>Date:</strong> {item.garnishment_date}</p>
              <p className="mt-1 text-sm"><strong>Value:</strong> ${item.value}</p>
              <p className="mt-2 text-sm"><strong>Description:</strong> {item.property_description}</p>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={() => {
          setCurrent({
            creditor: '',
            property_description: '',
            seizure_type: '',
            garnishment_date: '',
            value: ''
          });
          setAddModalOpen(true);
        }}
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        style={{ marginTop: '0.5rem' }}
      >
        Add Garnishment
      </button>

      {/* Add Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-xl relative">
            {loading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              </div>
            )}
            <h3 className="text-lg font-semibold mb-4">Add Garnishment</h3>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Creditor</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={current.creditor}
                  onChange={e => setCurrent(prev => ({ ...prev, creditor: e.target.value }))}
                >
                  <option value="">Select creditor</option>
                  {creditors.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Seizure Type</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={current.seizure_type}
                  onChange={e => setCurrent(prev => ({ ...prev, seizure_type: e.target.value }))}
                >
                  <option value="">Select type</option>
                  <option value="repo">Repo</option>
                  <option value="foreclosure">Foreclosure</option>
                  <option value="garnishment">Garnishment</option>
                  <option value="levy">Levy</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={current.garnishment_date}
                  onChange={e => setCurrent(prev => ({ ...prev, garnishment_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Value</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded px-2 py-1"
                  value={current.value}
                  onChange={e => setCurrent(prev => ({ ...prev, value: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Property Description</label>
              <textarea
                rows={2}
                className="w-full border rounded px-2 py-1"
                value={current.property_description}
                onChange={e => setCurrent(prev => ({ ...prev, property_description: e.target.value }))}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setAddModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={() => saveGarnishment(false)} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-xl relative">
            {loading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              </div>
            )}
            <h3 className="text-lg font-semibold mb-4">Edit Garnishment</h3>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Creditor</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={current.creditor}
                  onChange={e => setCurrent(prev => ({ ...prev, creditor: e.target.value }))}
                >
                  <option value="">Select creditor</option>
                  {creditors.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Seizure Type</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={current.seizure_type}
                  onChange={e => setCurrent(prev => ({ ...prev, seizure_type: e.target.value }))}
                >
                  <option value="">Select type</option>
                  <option value="repo">Repo</option>
                  <option value="foreclosure">Foreclosure</option>
                  <option value="garnishment">Garnishment</option>
                  <option value="levy">Levy</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={current.garnishment_date}
                  onChange={e => setCurrent(prev => ({ ...prev, garnishment_date: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Value</label>
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded px-2 py-1"
                  value={current.value}
                  onChange={e => setCurrent(prev => ({ ...prev, value: e.target.value }))}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-sm font-medium mb-1">Property Description</label>
              <textarea
                rows={2}
                className="w-full border rounded px-2 py-1"
                value={current.property_description}
                onChange={e => setCurrent(prev => ({ ...prev, property_description: e.target.value }))}
              />
            </div>
            <div className="flex justify-end space-x-2 mt-4">
              <button onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
              <button onClick={() => saveGarnishment(true)} className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </section>
);
}